import { ExtractComponentProp, FormValues } from '@approvalmax/types';
import { DatePicker, Form, Grid, NumberField, Select, TextField } from '@approvalmax/ui/src/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { standaloneConstants } from 'modules/common/constants';
import { useSelector } from 'modules/react-redux';
import { changeRequestName } from 'pages/requestForm/actions/actions';
import {
    changeStandaloneAmount,
    changeStandaloneCurrency,
    changeStandaloneDate,
} from 'pages/requestForm/actions/standalone';
import { getShowValidationErrors } from 'pages/requestForm/selectors/pageSelectors';
import { memo, useEffect } from 'react';
import bemFactory from 'react-bem-factory';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { z } from 'zod';

import { useCurrency, useDateChange } from './MainSection.hooks';
import { messages } from './MainSection.messages';
import { schema } from './MainSection.schema';
import { MainSectionProps } from './MainSection.types';

const qa = bemFactory.qa('reqf-standalone-request-form');

export const MainSection = memo<MainSectionProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();
    const showErrors = useSelector(getShowValidationErrors);
    const { isCrypto, currencies, isLoadingCurrencies } = useCurrency({
        companyId: request.companyId,
        requestId: request.id,
        currencyCode: request.currency,
    });

    const form = useForm<z.infer<typeof schema>>({
        values: {
            name: request.details.name,
            amount: request.amount,
            currencyId: request.currency,
            date: request.details.date,
        },
        mode: 'onChange',
        resetOptions: {
            keepErrors: true,
        },
        resolver: zodResolver(schema),
    });

    const handleDateChange = useDateChange(form.setValue);

    const handleChange: ExtractComponentProp<typeof Form<FormValues<typeof form>>, 'onChange'> = (values, rootName) => {
        switch (rootName) {
            case 'name': {
                dispatch(changeRequestName(values[rootName]));
                break;
            }
            case 'amount': {
                dispatch(changeStandaloneAmount(values[rootName]));
                break;
            }
            case 'currencyId': {
                const currency = currencies.find(({ id }) => id === values[rootName]);

                dispatch(changeStandaloneCurrency(currency ?? null));
                break;
            }
            case 'date': {
                dispatch(changeStandaloneDate(values[rootName]));
                break;
            }
        }
    };

    useEffect(() => {
        showErrors && form.trigger();
    }, [showErrors, form]);

    return (
        <Form form={form} onChange={handleChange}>
            <Grid gridTemplateColumns='1fr 190px 90px 160px' gap={24}>
                <TextField
                    name='name'
                    label={request.template.text || messages.nameLabel}
                    placeholder={messages.namePlaceholder}
                    maxLength={standaloneConstants.REQUEST_NAME_MAX_LENGTH}
                    required
                    qa={qa('request-name-editor')}
                />

                <NumberField
                    name='amount'
                    label={messages.amountLabel}
                    placeholder={messages.amountPlaceholder}
                    min={standaloneConstants.TOTAL_MIN_AMOUNT}
                    max={standaloneConstants.TOTAL_MAX_AMOUNT}
                    required
                    allowFloat
                    precision={isCrypto ? 8 : 2}
                    displayFormat='currency'
                    allowCalculation
                    clearable={false}
                    qa={qa('amount-editor')}
                />

                <Select
                    name='currencyId'
                    label={messages.currencyLabel}
                    placeholder={messages.currencyPlaceholder}
                    items={currencies}
                    itemNameKey='text'
                    required
                    dropdownWidth={200}
                    autocomplete
                    progress={isLoadingCurrencies}
                    qa={qa('currency-editor')}
                />

                <DatePicker
                    name='date'
                    label={messages.dateLabel}
                    placeholder={messages.datePlaceholder}
                    onChange={handleDateChange}
                    qa={qa('date-editor')}
                />
            </Grid>
        </Form>
    );
});
